import { useEffect, useState } from 'react';
import { ApiError, call } from './api';
import type { PlexClient, PlexItem } from './plex';
import { resolveTrack, setResolverPlex } from './musicResolve';
import { prefsNow } from './prefs';

// ---------- Volledige speler open/dicht ----------

let full = false;
const fullListeners = new Set<(v: boolean) => void>();
export function setFull(v: boolean) {
  full = v;
  fullListeners.forEach((l) => l(v));
}
export function useFullPlayer(): boolean {
  const [v, setV] = useState(full);
  useEffect(() => {
    fullListeners.add(setV);
    setV(full);
    return () => {
      fullListeners.delete(setV);
    };
  }, []);
  return v;
}

/**
 * Muziekspeler voor de hele app (blijft spelen als je verder bladert). Plex-nummers speelt de browser
 * rechtstreeks van de Plex-server; nummers uit addons via het doorgeefluik van tumedia.
 */

export interface MusicTrack {
  /** catalog = uit een lijst (Deezer, Apple Music, Last.fm); bij afspelen gezocht in Plex of je addons */
  source: 'plex' | 'addon' | 'catalog';
  isrc?: string | null;
  provider?: string;
  id: string;
  addonId?: string;
  addonName?: string;
  title: string;
  artist: string;
  album?: string | null;
  artwork?: string | null;
  /** Seconden */
  duration?: number | null;
  partKey?: string | null;
  ratingKey?: string | null;
  /** Al klaar afspeeladres (addon-lijsten met streamURL) */
  media?: string | null;
  /** Plex: pad van de hoes (zonder token), om te bewaren */
  thumbPath?: string | null;
  /** Hetzelfde nummer in andere addons (als deze bron faalt) */
  alternatives?: MusicTrack[];
  /** Codec of kwaliteit voor het label (FLAC, 320 kbps…) */
  format?: string | null;
}

export type Repeat = 'off' | 'all' | 'one';

interface State {
  queue: MusicTrack[];
  index: number;
  playing: boolean;
  loading: boolean;
  time: number;
  duration: number;
  shuffle: boolean;
  repeat: Repeat;
  volume: number;
  quality: string | null;
  error: string | null;
}

const VOL_KEY = 'tumedia.music.volume';
function storedVolume(): number {
  try {
    const v = Number(localStorage.getItem(VOL_KEY));
    return v > 0 && v <= 1 ? v : 0.8;
  } catch {
    return 0.8;
  }
}

let state: State = { queue: [], index: -1, playing: false, loading: false, time: 0, duration: 0, shuffle: false, repeat: 'off', volume: storedVolume(), quality: null, error: null };
let order: number[] = [];
let plexClient: PlexClient | null = null;
let audio: HTMLAudioElement | null = null;
let triedTranscode = false;
let loadToken = 0;
const listeners = new Set<(s: State) => void>();

function set(patch: Partial<State>) {
  state = { ...state, ...patch };
  listeners.forEach((l) => l(state));
}

export function setMusicPlex(p: PlexClient) {
  plexClient = p;
  setResolverPlex(p);
}

export function musicPlaying(): boolean {
  return state.playing;
}

export function current(): MusicTrack | null {
  return state.queue[state.index] ?? null;
}

function el(): HTMLAudioElement {
  if (audio) return audio;
  audio = new Audio();
  audio.preload = 'auto';
  audio.volume = state.volume;
  audio.addEventListener('timeupdate', () => {
    set({ time: audio!.currentTime });
    maybeScrobble();
  });
  audio.addEventListener('durationchange', () => set({ duration: Number.isFinite(audio!.duration) ? audio!.duration : current()?.duration ?? 0 }));
  audio.addEventListener('playing', () => set({ playing: true, loading: false, error: null }));
  audio.addEventListener('pause', () => set({ playing: false }));
  audio.addEventListener('waiting', () => set({ loading: true }));
  audio.addEventListener('ended', () => void onEnded());
  audio.addEventListener('error', () => void onError());
  return audio;
}

/** Plex: eerst het bestand zelf; kan de browser dat niet (bijv. ALAC), dan omgezet door Plex. */
function plexSrc(t: MusicTrack, transcode: boolean): string | null {
  if (!plexClient) return null;
  if (!transcode && t.partKey) return plexClient.url(t.partKey);
  if (!t.ratingKey) return null;
  return plexClient.url('/music/:/transcode/universal/start.mp3', {
    path: `/library/metadata/${t.ratingKey}`, mediaIndex: 0, partIndex: 0, protocol: 'http', directPlay: 0, directStream: 0, musicBitrate: 320, hasMDE: 1,
  }, true);
}

async function load(i: number, autoplay = true) {
  const original = state.queue[i];
  if (!original) return;
  const token = ++loadToken;
  triedTranscode = false;
  scrobbled = false;
  set({ index: i, loading: true, time: 0, duration: original.duration ?? 0, error: null, quality: original.format ?? null });
  // Nummer uit een lijst: eerst opzoeken in Plex of je addons
  let t = original;
  if (t.source === 'catalog') {
    const r = await resolveTrack(t);
    if (token !== loadToken) return;
    if (!r) {
      set({ loading: false, error: 'Niet gevonden in je Plex of je addons.' });
      window.setTimeout(() => {
        if (current() === original && state.error) next(true);
      }, 1500);
      return;
    }
    t = { ...r, artwork: r.artwork ?? original.artwork };
    const q = [...state.queue];
    q[i] = t;
    set({ queue: q, quality: t.format ?? null });
  }
  const a = el();
  let src: string | null = null;
  try {
    if (t.source === 'plex') src = plexSrc(t, false);
    else if (t.media) src = t.media;
    else if (t.addonId) {
      const r = await call<{ src: string; format: string | null; quality: string | null }>(`/api/music/${t.addonId}/stream/${encodeURIComponent(t.id)}`, { method: 'POST' });
      src = r.src;
      if (token === loadToken) set({ quality: [r.format, r.quality].filter(Boolean).join(' · ') || null });
    }
  } catch (err) {
    if (token !== loadToken) return;
    if (tryAlternative(i, t)) return;
    set({ loading: false, error: err instanceof ApiError && err.message !== err.code ? err.message : 'Dit nummer kon niet gestart worden.' });
    return;
  }
  if (token !== loadToken) return;
  if (!src) {
    set({ loading: false, error: 'Geen afspeeladres voor dit nummer.' });
    return;
  }
  a.src = src;
  if (autoplay) a.play().catch(() => set({ playing: false, loading: false }));
  void call('/api/music/recent', { method: 'POST', body: JSON.stringify({ track: storable(original.source === 'catalog' ? original : t) }) }).catch(() => undefined);
  void call('/api/lastfm/scrobble', { method: 'POST', body: JSON.stringify({ title: t.title, artist: t.artist, album: t.album, duration: t.duration, nowPlaying: true }) }).catch(() => undefined);
  startedAt = Math.round(Date.now() / 1000);
  mediaSession(t);
}

// Last.fm: geluisterd na de helft (of 4 minuten), alleen voor nummers langer dan 30 seconden
let scrobbled = false;
let startedAt = 0;
function maybeScrobble() {
  const t = current();
  if (!t || scrobbled || !audio) return;
  const dur = audio.duration || t.duration || 0;
  if (dur < 30 || audio.currentTime < Math.min(240, dur / 2)) return;
  scrobbled = true;
  void call('/api/lastfm/scrobble', { method: 'POST', body: JSON.stringify({ title: t.title, artist: t.artist, album: t.album, duration: Math.round(dur), timestamp: startedAt }) }).catch(() => undefined);
}

/** Bron faalt: hetzelfde nummer uit de volgende addon proberen. */
function tryAlternative(i: number, t: MusicTrack): boolean {
  const [alt, ...rest] = t.alternatives ?? [];
  if (!alt) return false;
  const q = [...state.queue];
  q[i] = { ...alt, artwork: alt.artwork ?? t.artwork, alternatives: rest };
  set({ queue: q, error: `${t.addonName ?? 'Addon'} lukt niet — probeer ${alt.addonName ?? 'volgende addon'}…` });
  void load(i);
  return true;
}

async function onError() {
  const t = current();
  if (!t) return;
  if (t.source === 'addon' && tryAlternative(state.index, t)) return;
  if (t.source === 'plex' && !triedTranscode && audio) {
    triedTranscode = true;
    const src = plexSrc(t, true);
    if (src) {
      audio.src = src;
      set({ quality: 'MP3 320 (omgezet door Plex)' });
      audio.play().catch(() => undefined);
      return;
    }
  }
  set({ loading: false, playing: false, error: 'Dit nummer kan niet worden afgespeeld.' });
  // Even wachten en dan door naar het volgende
  window.setTimeout(() => {
    if (current() === t && state.error) next();
  }, 2500);
}

async function onEnded() {
  const t = current();
  if (t?.source === 'plex' && t.ratingKey && plexClient) {
    void fetch(plexClient.url('/:/scrobble', { key: t.ratingKey, identifier: 'com.plexapp.plugins.library' }), { method: 'GET' }).catch(() => undefined);
  }
  if (state.repeat === 'one' && audio) {
    audio.currentTime = 0;
    void audio.play();
    return;
  }
  next(true);
}

function rebuildOrder(startAt: number) {
  const n = state.queue.length;
  order = [...Array(n).keys()];
  if (state.shuffle) {
    order = order.filter((i) => i !== startAt);
    for (let i = order.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [order[i], order[j]] = [order[j], order[i]];
    }
    order.unshift(startAt);
  }
}

export function playQueue(tracks: MusicTrack[], start = 0) {
  if (!tracks.length) return;
  // Afspelen van een nummer of album: meteen de volledige speler (instelbaar)
  if (prefsNow().playerAutoOpen) setFull(true);
  set({ queue: tracks });
  rebuildOrder(start);
  void load(start);
}

export function playNext(t: MusicTrack) {
  if (state.index < 0) return playQueue([t]);
  const q = [...state.queue];
  q.splice(state.index + 1, 0, t);
  set({ queue: q });
  rebuildOrder(state.index);
}

export function addToQueue(t: MusicTrack) {
  if (state.index < 0) return playQueue([t]);
  set({ queue: [...state.queue, t] });
  order.push(state.queue.length - 1);
}

export function toggle() {
  const a = el();
  if (!current()) return;
  if (a.paused) void a.play().catch(() => undefined);
  else a.pause();
}

export function next(auto = false) {
  const pos = order.indexOf(state.index);
  let n = order[pos + 1];
  if (n === undefined) {
    if (state.repeat === 'all' || !auto) n = order[0];
    else {
      set({ playing: false });
      return;
    }
  }
  if (n !== undefined) void load(n);
}

export function prev() {
  const a = el();
  if (a.currentTime > 3) {
    a.currentTime = 0;
    return;
  }
  const pos = order.indexOf(state.index);
  const p = order[pos - 1] ?? order[order.length - 1];
  if (p !== undefined) void load(p);
}

export function seek(sec: number) {
  if (audio) audio.currentTime = sec;
}

export function setVolume(v: number) {
  el().volume = v;
  set({ volume: v });
  try {
    localStorage.setItem(VOL_KEY, String(v));
  } catch {
    // privévenster
  }
}

export function toggleShuffle() {
  set({ shuffle: !state.shuffle });
  rebuildOrder(Math.max(0, state.index));
}

export function cycleRepeat() {
  set({ repeat: state.repeat === 'off' ? 'all' : state.repeat === 'all' ? 'one' : 'off' });
}

export function jumpTo(i: number) {
  void load(i);
}

export function stopMusic() {
  setFull(false);
  audio?.pause();
  audio?.removeAttribute('src');
  set({ queue: [], index: -1, playing: false, loading: false });
}

function mediaSession(t: MusicTrack) {
  if (!('mediaSession' in navigator)) return;
  try {
    navigator.mediaSession.metadata = new MediaMetadata({ title: t.title, artist: t.artist, album: t.album ?? '', artwork: t.artwork ? [{ src: t.artwork, sizes: '512x512' }] : [] });
    navigator.mediaSession.setActionHandler('play', () => toggle());
    navigator.mediaSession.setActionHandler('pause', () => toggle());
    navigator.mediaSession.setActionHandler('nexttrack', () => next());
    navigator.mediaSession.setActionHandler('previoustrack', () => prev());
  } catch {
    // niet overal ondersteund
  }
}

export function useMusic(): State {
  const [s, setS] = useState(state);
  useEffect(() => {
    listeners.add(setS);
    setS(state);
    return () => {
      listeners.delete(setS);
    };
  }, []);
  return s;
}

/** Wat de server mag bewaren (favorieten, playlists, verder luisteren): geen afspeeladressen. */
export function storable(t: MusicTrack) {
  return { source: t.source, id: t.id, addonId: t.addonId, isrc: t.isrc ?? null, title: t.title, artist: t.artist, album: t.album ?? null, artwork: t.source === 'plex' ? (t.thumbPath ? `plex:${t.thumbPath}` : null) : t.artwork ?? null, duration: t.duration ?? null, partKey: t.partKey ?? null, ratingKey: t.ratingKey ?? null };
}

/** Plex-nummer omzetten. */
export function fromPlexTrack(plex: PlexClient, i: PlexItem): MusicTrack {
  const media = i.Media?.[0];
  const part = media?.Part?.[0];
  const codec = media?.audioCodec?.toUpperCase();
  return {
    source: 'plex',
    id: i.ratingKey,
    ratingKey: i.ratingKey,
    title: i.title,
    artist: i.originalTitle ?? i.grandparentTitle ?? '',
    album: i.parentTitle ?? null,
    artwork: plex.image(i.parentThumb ?? i.thumb ?? i.grandparentThumb, 300, 300) ?? null,
    thumbPath: i.parentThumb ?? i.thumb ?? i.grandparentThumb ?? null,
    duration: i.duration ? Math.round(i.duration / 1000) : null,
    partKey: part?.key ?? null,
    format: codec ? `${codec}${media?.bitrate && codec !== 'FLAC' ? ` ${media.bitrate} kbps` : ''}` : null,
  };
}

/** Bewaard nummer weer bruikbaar maken (hoes uit Plex opnieuw met token). */
export function hydrate(plex: PlexClient, t: MusicTrack): MusicTrack {
  if (t.artwork?.startsWith('plex:')) {
    const path = t.artwork.slice(5);
    return { ...t, thumbPath: path, artwork: plex.image(path, 300, 300) ?? null };
  }
  return t;
}

export function fmtTime(sec: number | null | undefined): string {
  if (!sec || !Number.isFinite(sec)) return '0:00';
  const s = Math.floor(sec);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${r}` : `${m}:${r}`;
}
